import { useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { cartPersistenceService } from '../services/cartPersistence'

interface NavigationGuardOptions {
  hasActiveCart: boolean
  onNavigationAttempt?: (targetPath: string) => Promise<boolean> | boolean
  message?: string
}

export const useNavigationGuard = ({
  hasActiveCart,
  onNavigationAttempt,
  message = 'You have items in your cart. Are you sure you want to leave? Your cart will be saved automatically.'
}: NavigationGuardOptions) => {
  const location = useLocation()
  const navigate = useNavigate()

  // Handle browser back/forward/refresh
  useEffect(() => {
    if (!hasActiveCart) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveCart) {
        e.preventDefault()
        e.returnValue = message
        return message
      }
    }

    const handlePopState = (e: PopStateEvent) => {
      if (hasActiveCart) {
        const shouldContinue = confirm(message)
        if (!shouldContinue) {
          // Restore the current state
          window.history.pushState(null, '', location.pathname + location.search)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [hasActiveCart, message, location])

  // Create a guarded navigation function
  const guardedNavigate = useCallback(async (to: string) => {
    if (!hasActiveCart) {
      navigate(to)
      return true
    }

    // Check if custom handler exists
    if (onNavigationAttempt) {
      const shouldContinue = await onNavigationAttempt(to)
      if (shouldContinue) {
        navigate(to)
        return true
      }
      return false
    }

    // Default confirmation
    const shouldContinue = confirm(message)
    if (shouldContinue) {
      navigate(to)
      return true
    }
    return false
  }, [hasActiveCart, onNavigationAttempt, message, navigate])

  return {
    guardedNavigate,
    hasActiveCart
  }
}

